/**
 * CourseNavigation Component
 * Shared sidebar navigation for Authoring Preview and exported course packages.
 * Requirements: 1.1, 1.2, 1.3, 1.4, 7.1, 7.4
 */

import React from 'react';
import type { LessonProgress, LessonProgressStatus, ThemeTokens } from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';
import emtrainLogoDefault from '@course-authoring/shared-types/assets/logos/emtrain-default.png';
import { useTranslation, type TFunction } from '@course-authoring/i18n';
import { LessonProgressIndicator } from './LessonProgressIndicator';

export interface NavigationLesson {
  id: string;
  title: string;
}

export interface CourseNavigationProps {
  /** Course title */
  title: string;
  /** List of lessons */
  lessons: NavigationLesson[];
  /** Currently active lesson ID */
  currentLessonId: string | null;
  /** Map of lesson IDs to their progress state */
  lessonProgress: Record<string, LessonProgress>;
  /** Callback when a lesson is selected */
  onLessonSelect: (lessonId: string) => void;
  /** Optional styling override */
  className?: string;
  /** Optional style object override */
  style?: React.CSSProperties;
  /** Visual layout mode for the navigation sidebar */
  layout?: 'list' | 'statusCard' | 'assignmentPanel';
  /** Optional subtitle shown in statusCard layout when the active lesson is in progress. */
  estimatedTimeLabel?: string;
  /** When true, future lessons remain selectable even if earlier lessons are incomplete. */
  allowLessonSkipping?: boolean;
  /** When true, render the assignmentPanel sidebar in its course-complete state (green top border + "Completed" subtitle). */
  isCourseComplete?: boolean;
  /**
   * Timed-course requirement widget (NA-153) shown in the assignmentPanel
   * layout between the header and the lesson list — the player passes a
   * `CourseTimerBox` here for timed courses (in place of `estimatedTimeLabel`,
   * whose summed per-lesson estimate is misleading when a course-level
   * requirement is enforced).
   */
  timeRequirementSlot?: React.ReactNode;
  /** Optional test id */
  'data-testid'?: string;
}

/**
 * Component-layout values for the navigation status card. Inlined as part of
 * the Phase 4 component-token flatten — these are bespoke layout values
 * (colors and dimensions specific to this complex widget) not part of the
 * design-system token set. Where a value matches a primitive, the primitive
 * is used; where the value is bespoke, it lives here.
 */
function createStyles(tokens: ThemeTokens): Record<string, React.CSSProperties> {
  const statusCardTokens = {
    outerPadding: tokens.spacing.xl,                    // 24px
    sectionGap: tokens.spacing.md,                      // 12px
    cardBackgroundColor: '#F3F3F3',                     // bespoke (slightly off cardAlt)
    cardBorderRadius: tokens.radius.sm,                 // 6px
    rowBorderRadius: '0px',
    rowBorderColor: tokens.borders.default,             // #CACACA
    rowPaddingX: tokens.spacing.md,                     // 12px
    rowPaddingY: tokens.spacing.md,                     // 12px
    inProgressBackgroundColor: '#E1EBFF',               // bespoke (light accent tint)
    // NA-240/NA-242: the lesson sidebar does NOT track the account accent — the
    // in-progress indicator uses the fixed UI accent so it is stable across accounts.
    inProgressAccentColor: tokens.ui.fixedAccent,
    inProgressAccentWidth: tokens.spacing.sm,           // 8px
    completedIconColor: tokens.semantic.success,        // #1BA13F
    // Bespoke tint of completedIconColor into white, lighter than the ~18%
    // ratio used for inProgressBackgroundColor's tint of the accent color.
    completedActiveBackgroundColor: '#E0F2E5',
    notStartedIconGlyphColor: '#D0D3D8',                // bespoke neutral
    statusTextColor: tokens.text.primary,
    statusTextSize: tokens.typography.sizes.base,       // 16px
    statusTextWeight: tokens.typography.weights.regular,
    iconSize: '30px',                                   // bespoke (between 24 and 32)
    titleColor: tokens.text.primary,
    titleFontSize: tokens.typography.sizes.md,          // 20px
    titleLineHeight: '100%',
    titleLetterSpacing: '0px',
    titleWeight: tokens.typography.weights.bold,
    metaFontSize: tokens.typography.sizes.base,         // 16px
  };

  return {
    sidebar: {
      width: '100%',
      height: '100%',
      backgroundColor: tokens.backgrounds.sidebar,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: tokens.typography.fontFamily,
    },
    title: {
      margin: 0,
      padding: tokens.navigation.titlePadding,
      fontSize: tokens.navigation.titleFontSize,
      fontWeight: 600,
      color: tokens.navigation.titleColor,
      borderBottom: `1px solid ${tokens.navigation.borderColor}`,
    },
    emptyText: {
      padding: tokens.spacing.xl,
      fontSize: tokens.navigation.itemFontSize,
      color: tokens.navigation.emptyColor,
      textAlign: 'center',
    },
    lessonList: {
      flex: 1,
      overflow: 'auto',
      padding: tokens.navigation.listPadding,
      margin: 0,
      listStyle: 'none',
    },
    lessonItem: {
      marginBottom: tokens.spacing.xs,
    },
    lessonButton: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: tokens.navigation.itemGap,
      padding: tokens.navigation.itemPadding,
      fontSize: tokens.navigation.itemFontSize,
      color: tokens.navigation.itemColor,
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: tokens.navigation.itemBorderRadius,
      cursor: 'pointer',
      textAlign: 'left',
      transition: `background-color ${tokens.ui.transitionDefault}, color ${tokens.ui.transitionDefault}`,
    },
    lessonButtonActive: {
      backgroundColor: tokens.navigation.itemActiveBackground,
      color: tokens.navigation.itemActiveColor,
    },
    lessonButtonLocked: {
      opacity: tokens.navigation.itemLockedOpacity,
      cursor: 'not-allowed',
    },
    lessonTitleText: {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    lockIcon: {
      fontSize: tokens.navigation.lockIconSize,
      marginLeft: tokens.spacing.sm,
    },
    statusCardWrapper: {
      padding: statusCardTokens.outerPadding,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
    statusCard: {
      backgroundColor: statusCardTokens.cardBackgroundColor,
      borderRadius: statusCardTokens.cardBorderRadius,
      overflow: 'hidden',
    },
    statusCardCompleted: {
      borderTop: `9px solid ${tokens.completion.accent}`,
    },
    completedLabel: {
      margin: 0,
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.weights.regular,
      lineHeight: 1.4,
      color: tokens.completion.label,
    },
    statusCardHeader: {
      padding: `${statusCardTokens.outerPadding} ${statusCardTokens.outerPadding} 0`,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
    },
    statusCardTitle: {
      margin: 0,
      fontFamily: tokens.typography.fontFamily,
      fontSize: statusCardTokens.titleFontSize,
      fontWeight: statusCardTokens.titleWeight,
      color: statusCardTokens.titleColor,
      lineHeight: statusCardTokens.titleLineHeight,
      letterSpacing: statusCardTokens.titleLetterSpacing,
    },
    statusCardMeta: {
      margin: 0,
      fontSize: statusCardTokens.metaFontSize,
      color: tokens.text.primary,
      lineHeight: 1.4,
    },
    timeRequirementSlot: {
      padding: `${statusCardTokens.sectionGap} ${statusCardTokens.outerPadding} 0`,
      marginBottom: tokens.spacing.md,
    },
    statusCardBody: {
      padding: `${statusCardTokens.sectionGap} ${statusCardTokens.outerPadding} ${statusCardTokens.outerPadding}`,
    },
    assignmentLessonList: {
      padding: `${statusCardTokens.sectionGap} ${statusCardTokens.outerPadding} ${statusCardTokens.outerPadding}`,
      margin: 0,
      listStyle: 'none',
    },
    assignmentLessonItem: {
      marginBottom: '-1px',
    },
    statusButton: {
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
      padding: 0,
      borderRadius: statusCardTokens.rowBorderRadius,
      border: `1px solid ${statusCardTokens.rowBorderColor}`,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      cursor: 'pointer',
      textAlign: 'left',
      transition: `background-color ${tokens.ui.transitionDefault}, border-color ${tokens.ui.transitionDefault}`,
    },
    statusButtonInProgressAccent: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: statusCardTokens.inProgressAccentWidth,
      backgroundColor: statusCardTokens.inProgressAccentColor,
    },
    statusButtonCompletedActiveAccent: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: statusCardTokens.inProgressAccentWidth,
      backgroundColor: statusCardTokens.completedIconColor,
    },
    statusButtonContent: {
      position: 'relative',
      zIndex: 1,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: `${statusCardTokens.rowPaddingY} ${statusCardTokens.rowPaddingX}`,
      boxSizing: 'border-box',
    },
    statusButtonInProgress: {
      backgroundColor: statusCardTokens.inProgressBackgroundColor,
      borderColor: statusCardTokens.rowBorderColor,
    },
    statusButtonCompleted: {
      backgroundColor: 'transparent',
      borderColor: statusCardTokens.rowBorderColor,
    },
    statusButtonCompletedActive: {
      backgroundColor: statusCardTokens.completedActiveBackgroundColor,
      borderColor: statusCardTokens.rowBorderColor,
    },
    statusButtonNotStarted: {
      backgroundColor: 'transparent',
      borderColor: statusCardTokens.rowBorderColor,
    },
    statusIconBox: {
      width: statusCardTokens.iconSize,
      height: statusCardTokens.iconSize,
      minWidth: statusCardTokens.iconSize,
      borderRadius: '2px',
      border: '1px solid transparent',
      boxSizing: 'border-box',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    statusIconBoxInProgress: {
      backgroundColor: statusCardTokens.inProgressAccentColor,
      borderColor: statusCardTokens.inProgressAccentColor,
      color: tokens.text.onBrand,
    },
    statusIconBoxCompleted: {
      backgroundColor: statusCardTokens.completedIconColor,
      borderColor: statusCardTokens.completedIconColor,
      color: tokens.text.onBrand,
    },
    statusIconBoxNotStarted: {
      backgroundColor: 'transparent',
      borderColor: statusCardTokens.rowBorderColor,
      color: statusCardTokens.notStartedIconGlyphColor,
    },
    statusText: {
      minWidth: 0,
      fontSize: statusCardTokens.statusTextSize,
      fontWeight: statusCardTokens.statusTextWeight,
      lineHeight: 1.15,
      color: statusCardTokens.statusTextColor,
    },
    logoBand: {
      width: '100%',
      height: '95px',
      backgroundColor: tokens.backgrounds.sidebarLogo,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      // Pins the band to the bottom of the sidebar regardless of how much
      // content sits above. In the assignmentPanel branch the lesson list lives
      // in a separate flex:1 scroll region above this band, so the scrollbar
      // stops at the top of the band rather than running through it.
      marginTop: 'auto',
    },
    logoImg: {
      width: '215px',
      height: '70px',
      objectFit: 'contain',
      display: 'block',
    },
  };
}

function getStatusLabel(status: LessonProgressStatus, t: TFunction): string {
  if (status === 'completed') return t('course_navigation.completed');
  if (status === 'in_progress') return t('course_navigation.in_progress');
  return t('course_navigation.not_started');
}

const StatusCardIcon: React.FC<{ status: LessonProgressStatus }> = ({ status }) => {
  if (status === 'completed') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M3.33301 8.16667L6.49967 11.3333L12.6663 5.16667"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M5 3.66699L11.6667 8.00033L5 12.3337V3.66699Z" fill="currentColor" />
    </svg>
  );
};

// Helper component to render lesson item
const NavigationItem: React.FC<{
  lesson: NavigationLesson;
  isActive: boolean;
  progress: LessonProgress | undefined;
  percentageDisplay: number;
  onSelect: (lessonId: string) => void;
  isLocked: boolean;
  styles: ReturnType<typeof createStyles>;
}> = ({ lesson, isActive, progress, percentageDisplay, onSelect, isLocked, styles }) => (
  <li style={styles.lessonItem}>
    <button
      type="button"
      onClick={() => !isLocked && onSelect(lesson.id)}
      disabled={isLocked}
      style={{
        ...styles.lessonButton,
        ...(isActive ? styles.lessonButtonActive : {}),
        ...(isLocked ? styles.lessonButtonLocked : {}),
      }}
      aria-current={isActive ? 'true' : undefined}
      data-testid={`lesson-${lesson.id}`}
      aria-disabled={isLocked}
    >
      <LessonProgressIndicator
        status={progress?.status ?? 'not_started'}
        percentage={percentageDisplay}
        size={18}
        isActive={isActive}
      />
      <span style={styles.lessonTitleText}>{lesson.title}</span>
      {isLocked && <span style={styles.lockIcon} aria-hidden="true">🔒</span>}
    </button>
  </li>
);

const AssignmentPanelItem: React.FC<{
  lesson: NavigationLesson;
  isActive: boolean;
  progress: LessonProgress | undefined;
  onSelect: (lessonId: string) => void;
  isLocked: boolean;
  styles: ReturnType<typeof createStyles>;
}> = ({
  lesson,
  isActive,
  progress,
  onSelect,
  isLocked,
  styles,
}) => {
  const status = progress?.status ?? 'not_started';
  // Blue in-progress is exclusive to the active, not-yet-completed lesson.
  // A completed lesson always renders completed (even when not active), but
  // gets its own green accent + tint below when it's also the active lesson,
  // so learners can tell which completed lesson they're currently viewing.
  const visualStatus =
    status === 'completed'
      ? 'completed'
      : isActive && status === 'in_progress'
        ? 'in_progress'
        : 'not_started';
  const isActiveCompleted = isActive && visualStatus === 'completed';
  const rowStyle = {
    ...styles.statusButton,
    ...(visualStatus === 'completed' ? styles.statusButtonCompleted : {}),
    ...(visualStatus === 'in_progress' ? styles.statusButtonInProgress : {}),
    ...(visualStatus === 'not_started' ? styles.statusButtonNotStarted : {}),
    ...(isActiveCompleted ? styles.statusButtonCompletedActive : {}),
    ...(isLocked ? styles.lessonButtonLocked : {}),
  };
  const iconStyle = {
    ...styles.statusIconBox,
    ...(visualStatus === 'completed' ? styles.statusIconBoxCompleted : {}),
    ...(visualStatus === 'in_progress' ? styles.statusIconBoxInProgress : {}),
    ...(visualStatus === 'not_started' ? styles.statusIconBoxNotStarted : {}),
  };

  return (
    <li style={styles.assignmentLessonItem}>
      <button
        type="button"
        onClick={() => !isLocked && onSelect(lesson.id)}
        disabled={isLocked}
        style={rowStyle}
        aria-current={isActive ? 'true' : undefined}
        aria-disabled={isLocked}
        data-testid={`lesson-status-${lesson.id}`}
        data-status={visualStatus}
      >
        {visualStatus === 'in_progress' || isActiveCompleted ? (
          <span
            style={isActiveCompleted ? styles.statusButtonCompletedActiveAccent : styles.statusButtonInProgressAccent}
            aria-hidden="true"
            data-testid={`lesson-status-accent-${lesson.id}`}
          />
        ) : null}
        <span style={styles.statusButtonContent}>
          <span style={iconStyle} data-testid={`lesson-status-icon-${lesson.id}`}>
            <StatusCardIcon status={visualStatus} />
          </span>
          <span style={styles.statusText}>{lesson.title}</span>
        </span>
      </button>
    </li>
  );
};

export const CourseNavigation: React.FC<CourseNavigationProps> = (props) => {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const styles = React.useMemo(() => createStyles(tokens), [tokens]);
  const { title, lessons, style, className, layout = 'list' } = props;

  // Determine locked status for each lesson
  // A lesson is locked if the PREVIOUS lesson is not completed.
  // Exception: First lesson is always unlocked.
  const lockedStatusMap = React.useMemo(() => {
    const map: Record<string, boolean> = {};
    if (props.allowLessonSkipping || props.isCourseComplete) {
      for (const lesson of lessons ?? []) {
        map[lesson.id] = false;
      }
      return map;
    }

    let previousCompleted = true; // First lesson is accessible

    if (!lessons) return map;

    for (const lesson of lessons) {
      // If previous was NOT complete, this one is locked
      map[lesson.id] = !previousCompleted;

      // Update for next: is THIS lesson complete?
      const progress = props.lessonProgress[lesson.id];
      const isComplete = progress?.status === 'completed';

      // If this lesson is NOT complete, the NEXT one will be locked
      if (!isComplete) {
        previousCompleted = false;
      }
    }
    return map;
  }, [lessons, props.allowLessonSkipping, props.isCourseComplete, props.lessonProgress]);

  // Logo footer band — rendered in every CourseNavigation layout EXCEPT the
  // plain `list` layout, which has different chrome. See NA-102.
  const primaryLogoUrl = tokens.assets?.primaryLogoUrl;
  const sidebarFooter =
    layout !== 'list' ? (
      <div style={styles.logoBand} data-testid="course-navigation-logo-band">
        <img
          src={primaryLogoUrl ?? emtrainLogoDefault}
          alt="Logo"
          style={styles.logoImg}
          onError={(e) => {
            // Fall back to the default logo once; guard against infinite loop
            // by only switching when the src is not already the default.
            const img = e.currentTarget;
            if (img.src !== emtrainLogoDefault) {
              img.src = emtrainLogoDefault;
            }
          }}
        />
      </div>
    ) : null;

  if (!lessons || lessons.length === 0) {
    return (
      <nav style={{ ...styles.sidebar, ...style }} className={className} aria-label={t('course_navigation.aria_nav')}>
        <h2 style={styles.title}>{title}</h2>
        <p style={styles.emptyText}>{t('course_navigation.no_lessons')}</p>
        {sidebarFooter}
      </nav>
    );
  }

  if (layout === 'statusCard') {
    const activeLesson = lessons.find((lesson) => lesson.id === props.currentLessonId) ?? lessons[0];
    if (!activeLesson) {
      return (
        <nav style={{ ...styles.sidebar, ...style }} className={className} aria-label={t('course_navigation.aria_nav')}>
          <h2 style={styles.title}>{title}</h2>
          <p style={styles.emptyText}>{t('course_navigation.no_lessons')}</p>
        </nav>
      );
    }

    const lessonProgress = props.lessonProgress[activeLesson.id];
    const estimatedTimeLabel = props.estimatedTimeLabel?.trim();
    const status = lessonProgress?.status ?? 'not_started';

    const shouldShowEstimatedTime = status === 'in_progress' && Boolean(estimatedTimeLabel);
    const statusButtonStyle = {
      ...styles.statusButton,
      ...(status === 'completed'
        ? styles.statusButtonCompleted
        : status === 'in_progress'
          ? styles.statusButtonInProgress
          : styles.statusButtonNotStarted),
    };
    const statusIconBoxStyle = {
      ...styles.statusIconBox,
      ...(status === 'completed'
        ? styles.statusIconBoxCompleted
        : status === 'in_progress'
          ? styles.statusIconBoxInProgress
          : styles.statusIconBoxNotStarted),
    };

    return (
      <nav
        style={{ ...styles.sidebar, ...style }}
        className={className}
        aria-label={t('course_navigation.aria_nav')}
        data-testid={props['data-testid'] ?? 'course-navigation'}
      >
        <div style={styles.statusCardWrapper}>
          <section style={styles.statusCard}>
            <div style={styles.statusCardHeader}>
              <h2 style={styles.statusCardTitle}>{activeLesson.title}</h2>
              {shouldShowEstimatedTime ? <p style={styles.statusCardMeta}>{estimatedTimeLabel}</p> : null}
            </div>

            <div style={styles.statusCardBody}>
              <button
                type="button"
                onClick={() => props.onLessonSelect(activeLesson.id)}
                style={statusButtonStyle}
                aria-current="true"
                data-testid={`lesson-status-${activeLesson.id}`}
                data-status={status}
              >
                {status === 'in_progress' ? (
                  <span
                    style={styles.statusButtonInProgressAccent}
                    aria-hidden="true"
                    data-testid={`lesson-status-accent-${activeLesson.id}`}
                  />
                ) : null}
                <span style={styles.statusButtonContent}>
                  <span
                    style={statusIconBoxStyle}
                    data-testid={`lesson-status-icon-${activeLesson.id}`}
                  >
                    <StatusCardIcon status={status} />
                  </span>
                  <span style={styles.statusText}>{getStatusLabel(status, t)}</span>
                </span>
              </button>
            </div>
          </section>
        </div>
        {sidebarFooter}
      </nav>
    );
  }

  if (layout === 'assignmentPanel') {
    const estimatedTimeLabel = props.estimatedTimeLabel?.trim();
    const isCourseComplete = props.isCourseComplete ?? false;
    const cardStyle = isCourseComplete
      ? { ...styles.statusCard, ...styles.statusCardCompleted }
      : styles.statusCard;

    return (
      <nav
        style={{ ...styles.sidebar, ...style }}
        className={className}
        aria-label={t('course_navigation.aria_nav')}
        data-testid={props['data-testid'] ?? 'course-navigation'}
        data-course-complete={isCourseComplete ? 'true' : 'false'}
      >
        <div
          style={{
            ...styles.statusCardWrapper,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            // Standard scrollbar-color (thumb then track), set inline per the
            // inline-styles-only rule. Semi-transparent values let the themed
            // panel background show through: thumb #fff@50%, track #000@50%.
            scrollbarColor: 'rgba(255, 255, 255, 0.5) rgba(0, 0, 0, 0.5)',
          }}
        >
          <section style={{ ...cardStyle, flexShrink: 0 }}>
            <div style={styles.statusCardHeader}>
              <h2 style={styles.statusCardTitle}>{title}</h2>
              {isCourseComplete ? (
                <p style={styles.completedLabel} data-testid="course-navigation-completed-label">
                  {t('course_navigation.completed')}
                </p>
              ) : null}
              {!isCourseComplete && estimatedTimeLabel ? (
                <p style={styles.statusCardMeta}>{estimatedTimeLabel}</p>
              ) : null}
            </div>
            {props.timeRequirementSlot ? (
              <div style={styles.timeRequirementSlot}>{props.timeRequirementSlot}</div>
            ) : null}
            <ul style={styles.assignmentLessonList} role="list">
              {lessons.map((lesson) => (
                <AssignmentPanelItem
                  key={lesson.id}
                  lesson={lesson}
                  isActive={props.currentLessonId === lesson.id}
                  progress={props.lessonProgress[lesson.id]}
                  onSelect={props.onLessonSelect}
                  isLocked={lockedStatusMap[lesson.id] ?? false}
                  styles={styles}
                />
              ))}
            </ul>
          </section>
        </div>
        {sidebarFooter}
      </nav>
    );
  }

  return (
    <nav
      style={{ ...styles.sidebar, ...style }}
      className={className}
      aria-label={t('course_navigation.aria_nav')}
      data-testid={props['data-testid'] ?? 'course-navigation'}
    >
      <h2 style={styles.title}>{title}</h2>
      <ul style={styles.lessonList} role="list">
        {lessons.map((lesson) => {
          const isActive = props.currentLessonId === lesson.id;
          const progress = props.lessonProgress[lesson.id];
          const percentageDisplay = progress ? progress.percentage * 100 : 0;
          const isLocked = lockedStatusMap[lesson.id] ?? false;

          return (
            <NavigationItem
              key={lesson.id}
              lesson={lesson}
              isActive={isActive}
              progress={progress}
              percentageDisplay={percentageDisplay}
              onSelect={props.onLessonSelect}
              isLocked={isLocked}
              styles={styles}
            />
          );
        })}
      </ul>
    </nav>
  );
};
